import {
  Sheet,
  SheetTitle,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
  SheetContent,
  SheetDescription,
} from '@design-system/Sheet';

import { cn } from '@/utils/styles';
import { FilterIcon } from '@/components/icons/FilterIcon';
import { Button, ButtonProps } from '@design-system/Button';
import React, { useCallback, useMemo, useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { EventTracker } from '@/utils';

type IFilterValue = Record<number, string[] | undefined>;

export interface IFilter {
  id: number;
  title: string;
  subgroup: string[];
}

export interface IFilterOption {
  filter: number;
  value: string[] | undefined;
}

const renderFilterOptions = (options: string[]) => {
  if (!Array.isArray(options) || !options.length) return null;

  const renderedFilterOptions = options.map((option) => (
    <ToggleGroupItem
      key={option}
      value={option}
      onClick={() => {
        EventTracker.trackEvent(
          'nutrition',
          `nutrition_myFoods_filter_${option.replace(/\s+/g, '')}`,
        );
      }}
    >
      {option}
    </ToggleGroupItem>
  ));

  return renderedFilterOptions;
};

const renderFilters = (
  filterValue: IFilterValue,
  filters: IFilter[] | undefined,
  onValueChange: (option: IFilterOption) => void,
) => {
  if (!Array.isArray(filters) || !filters.length) return null;

  const renderedFilters = filters.map((filter) => (
    <div className="flex flex-col gap-4" key={filter.title}>
      {filter.title && (
        <span className="typography-h3 text-secondary">{filter.title}</span>
      )}

      <ToggleGroup
        type="multiple"
        variant="outline"
        value={filterValue[filter.id] ?? []}
        onValueChange={(value) => onValueChange({ filter: filter.id, value })}
      >
        {renderFilterOptions(filter.subgroup)}
      </ToggleGroup>
    </div>
  ));

  return renderedFilters;
};

const getFilterCount = (filterValue?: IFilterValue) => {
  if (!filterValue) return 0;

  const filterCount = Object.values(filterValue).reduce((acc, value) => {
    acc += value?.length ?? 0;

    return acc;
  }, 0);

  return filterCount;
};

export const Filter = Sheet;

Filter.displayName = 'Filter';

export interface FilterTriggerProps extends ButtonProps {
  isError?: boolean;
  isLoading?: boolean;
  initialValue?: IFilterValue;
}

export const FilterTrigger = ({
  isError,
  isLoading,
  className,
  initialValue,
  ...rest
}: FilterTriggerProps) => {
  const appliedFilterCount = getFilterCount(initialValue);
  const isActive = !!appliedFilterCount;

  return (
    <SheetTrigger asChild>
      <Button
        variant="toggle"
        onClick={() => {
          EventTracker.trackEvent('nutrition', 'nutrition_myFoods_chip_filter');
        }}
        {...rest}
        data-focus={isActive}
        disabled={isLoading || isError}
        className={cn('typography-body h-9 pr-4 pl-2 text-primary', className)}
      >
        <FilterIcon width={24} height={24} className="!size-5" />
        Filter
        {!!appliedFilterCount && <span>({appliedFilterCount})</span>}
      </Button>
    </SheetTrigger>
  );
};

export interface FilterContentProps {
  isError?: boolean;
  filters?: IFilter[];
  isLoading?: boolean;
  children?: React.ReactNode;
  initialValue?: IFilterValue;
  onSubmit?: (value: IFilterValue) => void;
  productId?: number;
}

export const FilterContent = ({
  filters,
  children,
  onSubmit,
  initialValue = {},
}: FilterContentProps) => {
  const [filterValue, setFilterValue] = useState<IFilterValue>(initialValue);

  const selectedFilterCount = getFilterCount(filterValue);

  const handleOnValueChange = useCallback((option: IFilterOption) => {
    setFilterValue((value) => ({ ...value, [option.filter]: option.value }));
  }, []);

  const handleOnSubmit = () => {
    if (typeof onSubmit !== 'function') return;

    onSubmit(filterValue);
  };

  const handleOnReset = () => {
    setFilterValue({});
  };

  const renderedFilters = useMemo(
    () => renderFilters(filterValue, filters, handleOnValueChange),
    [filterValue, filters, handleOnValueChange],
  );

  return (
    <SheetContent variant="glass">
      <SheetHeader>
        <SheetTitle className="typography-h1 text-secondary">Filter</SheetTitle>
      </SheetHeader>

      <SheetDescription asChild>
        <div className="flex flex-col gap-8 overflow-scroll">
          {renderedFilters}

          {children}
        </div>
      </SheetDescription>

      <SheetFooter className="mt-auto flex flex-row gap-4">
        <Button className="w-full" variant="secondary" onClick={handleOnReset}>
          Reset
        </Button>

        <SheetTrigger asChild>
          <Button className="w-full" onClick={handleOnSubmit}>
            Apply
            {!!selectedFilterCount && <span>({selectedFilterCount})</span>}
          </Button>
        </SheetTrigger>
      </SheetFooter>
    </SheetContent>
  );
};
